import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import SignaturePad from "../components/SignaturePad";
import { Loader2, Check, FileText, AlertCircle, Building2, User, Calendar, Euro, Copy, MessageCircle, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientSignaturePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [depositInfo, setDepositInfo] = useState(null);
  const [ibanCopied, setIbanCopied] = useState(false);

  useEffect(() => {
    fetchQuoteData();
  }, [token]);

  const fetchQuoteData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/sign/${token}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Lien invalide");
      }
      
      const data = await response.json();
      
      if (data.already_signed) {
        setAlreadySigned(true);
        setSignedAt(data.signed_at);
      } else {
        setQuoteData(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignature = async (signatureData) => {
    try {
      setSubmitting(true);
      
      const response = await fetch(`${API_URL}/api/sign/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_data: signatureData })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la signature");
      }
      
      const data = await response.json();
      setDepositInfo(data.deposit_invoice);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const copyIban = () => {
    if (depositInfo?.iban) {
      navigator.clipboard.writeText(depositInfo.iban.replace(/\s/g, ''));
      setIbanCopied(true);
      setTimeout(() => setIbanCopied(false), 2000);
    }
  };
  
  const sendPaymentProof = () => {
    const phone = depositInfo?.agency_phone?.replace(/\s/g, '').replace(/^0/, '33');
    const amount = depositInfo?.total?.toFixed(2) || '0';
    const invoiceNumber = depositInfo?.document_number || '';
    const message = `Bonjour, je vous envoie la preuve de virement de ${amount}€ pour la facture ${invoiceNumber}.`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-zinc-400">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold mb-2">Lien invalide</h1>
          <p className="text-zinc-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Already signed state
  if (alreadySigned) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="text-xl font-bold mb-2">Devis déjà signé</h1>
          <p className="text-zinc-400 text-sm">
            Ce devis a été signé le {new Date(signedAt).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
    );
  }

  // Success state with payment info
  if (success) {
    const depositAmount = depositInfo?.total || ((quoteData?.quote?.total || 0) * (quoteData?.quote?.deposit_percentage || 30) / 100);
    const iban = depositInfo?.iban;
    const agencyPhone = depositInfo?.agency_phone;
    const companyName = quoteData?.company?.name || quoteData?.quote?.agency?.name || "L'artisan";
    
    return (
      <div className="min-h-screen bg-zinc-950 text-white p-4">
        <div className="max-w-md mx-auto">
          {/* Success header */}
          <div className="text-center mb-8 pt-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Check className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Merci !</h1>
            <p className="text-zinc-400">
              Votre signature a été enregistrée avec succès.
            </p>
          </div>
          
          {/* Deposit amount card */}
          <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 mb-6">
            <div className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
              <CreditCard className="w-4 h-4" />
              <span>Acompte à régler</span>
            </div>
            <p className="text-3xl font-bold text-primary mb-1">
              {depositAmount.toFixed(2)} €
            </p>
            <p className="text-xs text-zinc-500">
              {quoteData?.quote?.deposit_percentage || 30}% du montant total
            </p>
            
            {depositInfo?.document_number && (
              <p className="text-xs text-zinc-400 mt-3">
                Facture N° <span className="font-mono">{depositInfo.document_number}</span>
              </p>
            )}
          </div>
          
          {/* IBAN section */}
          {iban && (
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">Coordonnées bancaires</p>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="gap-2 text-xs"
                  onClick={copyIban}
                >
                  {ibanCopied ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-500" />
                      Copié !
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copier l'IBAN
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <p className="text-xs text-zinc-400 mb-1">IBAN</p>
                <p className="font-mono text-sm break-all select-all">
                  {iban}
                </p>
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Effectuez un virement vers ce compte pour régler votre acompte.
              </p>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="space-y-3">
            {agencyPhone && (
              <Button 
                className="w-full gap-3 bg-[#25D366] hover:bg-[#20BD5A] py-6"
                onClick={sendPaymentProof}
              >
                <MessageCircle className="w-5 h-5" />
                Envoyer la preuve de virement
              </Button>
            )}
          </div>
          
          {/* Email notification */}
          <div className="mt-8 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
            <p className="text-sm text-zinc-400 text-center">
              📧 Vous allez recevoir un email de confirmation avec les documents en pièces jointes.
            </p>
          </div>
          
          {/* Footer */}
          <p className="text-xs text-zinc-600 text-center mt-8">
            Merci de votre confiance — {companyName}
          </p>
        </div>
      </div>
    );
  }

  // Main signature view
  const quote = quoteData?.quote;
  const company = quoteData?.company;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="p-4 border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-lg sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {company?.logo_url ? (
            <img src={company.logo_url} alt="Logo" className="h-10 w-auto rounded" />
          ) : (
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
          )}
          <div>
            <p className="font-semibold">{company?.name || quote?.agency?.name}</p>
            <p className="text-xs text-zinc-400">Devis N° {quote?.document_number}</p>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-lg mx-auto pb-32">
        {/* Client info */}
        <div className="mb-6 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-2 text-sm text-zinc-400 mb-1">
            <User className="w-4 h-4" />
            Client
          </div>
          <p className="font-semibold">{quote?.client?.name}</p>
          {quote?.client?.email && (
            <p className="text-sm text-zinc-400">{quote?.client?.email}</p>
          )}
        </div>

        {/* Quote details */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Détail du devis
          </h2>
          
          <div className="space-y-2">
            {quote?.items?.map((item, index) => (
              <div 
                key={index}
                className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800 flex justify-between items-start gap-4"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{item.name}</p>
                  {item.description && (
                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{item.description}</p>
                  )}
                  <p className="text-xs text-zinc-400 mt-1">
                    {item.price?.toFixed(2)} € × {item.quantity}
                  </p>
                </div>
                <p className="font-mono font-semibold text-sm whitespace-nowrap">
                  {(item.price * item.quantity).toFixed(2)} €
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="mb-6 p-4 bg-zinc-900 rounded-xl border border-zinc-700">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Sous-total HT</span>
              <span>{quote?.subtotal?.toFixed(2)} €</span>
            </div>
            {quote?.is_subject_to_vat && (
              <div className="flex justify-between">
                <span className="text-zinc-400">TVA ({quote?.tva_rate}%)</span>
                <span>{quote?.tva_amount?.toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-zinc-700 text-lg font-bold">
              <span>Total TTC</span>
              <span className="text-primary">{quote?.total?.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Deposit info */}
        <div className="mb-6 p-4 bg-primary/10 rounded-xl border border-primary/30">
          <div className="flex items-center gap-2 text-sm mb-2">
            <Euro className="w-4 h-4 text-primary" />
            <span className="font-medium">Acompte à la signature</span>
          </div>
          <div className="flex justify-between items-end">
            <div>
              <p className="text-2xl font-bold text-primary">
                {((quote?.total || 0) * (quote?.deposit_percentage || 30) / 100).toFixed(2)} €
              </p>
              <p className="text-xs text-zinc-400">
                {quote?.deposit_percentage || 30}% du montant total
              </p>
            </div>
            <div className="text-right text-xs text-zinc-400">
              <p>Solde restant</p>
              <p className="font-medium">
                {((quote?.total || 0) * (1 - (quote?.deposit_percentage || 30) / 100)).toFixed(2)} €
              </p>
            </div>
          </div>
        </div>

        {/* Validity */}
        <div className="mb-8 flex items-center gap-2 text-sm text-zinc-400">
          <Calendar className="w-4 h-4" />
          <span>
            Devis valable {quote?.validity_days || 30} jours
          </span>
        </div>

        {/* Signature section */}
        <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-4">
          <h2 className="text-lg font-semibold mb-1 text-center">Signature</h2>
          <p className="text-sm text-zinc-400 text-center mb-4">
            En signant, vous acceptez ce devis et vous engagez à verser l'acompte.
          </p>
          
          <SignaturePad
            width={Math.min(350, window.innerWidth - 64)}
            height={160}
            onSave={handleSignature}
          />
          
          {submitting && (
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Enregistrement de votre signature...
            </div>
          )}
        </div>

        {/* Legal mentions */}
        <p className="mt-6 text-xs text-zinc-500 text-center">
          En signant ce devis, vous acceptez les conditions générales de vente de {company?.name || quote?.agency?.name}.
          {quote?.notes && <span className="block mt-2">Note : {quote.notes}</span>}
        </p>
      </main>
    </div>
  );
}
